"use client"

import styled from "@emotion/styled"
import { useLocale } from "next-intl"
import Image from "next/image"
import { type KeyboardEvent, useEffect, useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import { useInViewConfig } from "ui/transitions/utils"
import { extractProperty, getLinkHrefFromMultiUrlPickerItem, getUmbracoMediaUrl } from "utils"
import { BRUSH_UNDERLINE_URI } from "../brushUnderline"
import { handleAnchorScroll } from "../scrollToAnchor"
import { LANDING_ELEMENTS, useLandingAnalytics } from "../analytics"

type Props = {
  data: any
}

// --- Icons ---

function PauseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="#000" aria-hidden focusable="false">
      <rect x="3" y="1" width="4" height="14" rx="1" />
      <rect x="9" y="1" width="4" height="14" rx="1" />
    </svg>
  )
}

function PlayIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="#000" aria-hidden focusable="false">
      <path d="M3 1.5v13l11-6.5L3 1.5z" />
    </svg>
  )
}

function ArrowIcon({ dir }: { dir: "left" | "right" }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
      <path
        d={dir === "left" ? "M20 12H4M10 6l-6 6 6 6" : "M4 12h16M14 6l6 6-6 6"}
        stroke="#000"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// --- Styled Components ---

const Section = styled.section`
  background-color: var(--comp-bg);
  color: var(--comp-text);
  overflow: hidden;
  padding: 40px 20px;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 64px clamp(24px, 8.33vw, 120px);
  }
`

/* mobile: content (logos + text) then media stacked. desktop: media on the left,
   content on the reading-start (right) side — the Figma "V2_Left" variant. */
const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 40px;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    align-items: center;
    gap: 64px;
  }
`

/* first in JSX = reading-start (right) side in RTL */
const TextColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  text-align: start;
  min-width: 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex: 1 1 0;
  }
`

const Logos = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 24px;
`

/* fixed logo height per Figma (66px display, sourced from a 2x upload) */
const Logo = styled.img`
  height: 66px;
  width: auto;
  max-width: 107px;
  object-fit: contain;
`

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

/* Rich-text project name: the emphasized word carries the Figma brush underline. */
const Title = styled.h2`
  font-size: 40px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--comp-heading);
  margin: 0;

  p {
    margin: 0;
    display: inline;
  }
  strong,
  b {
    position: relative;
    display: inline-block;
    font-weight: 700;
    text-decoration: none;
  }
  strong::after,
  b::after {
    content: "";
    position: absolute;
    inset-inline: 0;
    bottom: -4px;
    height: 6px;
    background-color: currentColor;
    -webkit-mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
    mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 60px;
    font-weight: 500;

    strong,
    b {
      font-weight: 700;
    }
    strong::after,
    b::after {
    }
  }
`

const MarketingHeadline = styled.p`
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--comp-text);
  margin: 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 24px;
  }
`

const Description = styled.p`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: var(--comp-text);
  margin: 0;
  white-space: pre-line;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 18px;
  }
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 24px;
  }
`

/* button 1 = Outline, button 2 = Filled — fixed front-end rule by order (ASH-1889) */
const CtaButton = styled.a<{ $filled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  /* Mobile: the buttons share the row and together span the content width, per
     Figma. They used to size to their text and left a gap beside them
     (ASH-2127). From md they hug their text again. */
  flex: 1 1 0;
  min-width: 0;
  min-height: 36px;
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  /* buttons follow the Inner Color Package (CLAUDE.md), falling back to --comp-* */
  border: 1px solid var(--inner-heading, var(--comp-heading));
  background-color: ${({ $filled }) =>
    $filled ? "var(--inner-heading, var(--comp-heading))" : "transparent"};
  color: ${({ $filled }) =>
    $filled ? "var(--inner-bg, var(--comp-bg))" : "var(--inner-heading, var(--comp-heading))"};
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 2px dashed var(--inner-heading, var(--comp-heading));
    outline-offset: 2px;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    flex: unset;
    min-height: 48px;
    font-size: 20px;
    padding: 8px 24px;
  }
`

/* Staggered entrance, same timings and 140ms cadence as AboutOpening1
   (ASH-2125). Order here leads with the media, then the text top-down. */
const Reveal = styled.div<{ $in: boolean; $delay: number }>`
  opacity: ${({ $in }) => ($in ? 1 : 0)};
  transform: translateY(${({ $in }) => ($in ? "0" : "40px")});
  transition: opacity 0.7s ease, transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: ${({ $delay }) => $delay}ms;
  will-change: opacity, transform;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

/* the reveal wrapper is the flex child so it carries the column sizing */
const MediaSlot = styled(Reveal)`
  ${(props) => props.theme.breakpoints.up("md")} {
    flex: 1 1 0;
    min-width: 0;
  }
`

/* square 680x680 media per Figma */
const MediaColumn = styled.div`
  position: relative;
  aspect-ratio: 1 / 1;
  border-radius: 8px;
  overflow: hidden;
`

const Slide = styled.div<{ $active: boolean }>`
  position: absolute;
  inset: 0;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transition: opacity 0.6s ease;
  pointer-events: ${({ $active }) => ($active ? "auto" : "none")};
`

const MediaImage = styled(Image)`
  object-fit: cover;
`

const MediaVideo = styled.video`
  position: absolute;
  inset: 0;
  height: 100%;
  object-fit: cover;
`

/* The single-video branch keeps its control overlaid on the image. */
const VideoControls = styled.div`
  position: absolute;
  bottom: 16px;
  inset-inline-start: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  z-index: 2;
`

const RoundButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid #000;
  background: #fff;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
  &:focus-visible {
    outline: 2px dashed #fff;
    outline-offset: 2px;
  }
`

const PlayPauseBtn = styled(RoundButton)`
  border: none;
`

/* Figma sizes the gallery arrows smaller than the play/pause between them. */
const ArrowButton = styled(RoundButton)`
  width: 32px;
  height: 32px;
`

/* The gallery control row owns the inline-end corner, so the per-slide video
   button takes the opposite one and never collides with it (ASH-2124). */
const GalleryPlayPauseBtn = styled(RoundButton)`
  position: absolute;
  bottom: 16px;
  inset-inline-start: 16px;
  z-index: 3;
  border: none;
`

/* Gallery chrome sits outside the image box (ASH-2128). Mobile stacks it under
   the image: arrows + play/pause first, then the dots. From md the arrow row
   overlays the image corner and the dots become a vertical column beside it. */
const MediaFrame = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  ${(props) => props.theme.breakpoints.up("md")} {
    /* 8px dot column + 16px gap, so the dots clear the image */
    padding-inline-end: 24px;
  }
`

const GalleryControls = styled.div`
  display: flex;
  align-self: center;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
  z-index: 2;

  ${(props) => props.theme.breakpoints.up("md")} {
    position: absolute;
    bottom: 16px;
    /* 24px of frame padding + the 16px inset inside the image */
    inset-inline-end: 40px;
    margin: 0;
  }
`

const Dots = styled.div`
  display: flex;
  align-self: center;
  align-items: center;
  gap: 8px;
  margin-top: 24px;

  ${(props) => props.theme.breakpoints.up("md")} {
    position: absolute;
    inset-inline-end: 0;
    top: 50%;
    transform: translateY(-50%);
    flex-direction: column;
    margin: 0;
  }
`

/* Dots now sit on the section background rather than over the image, so they
   follow the colour package instead of being hardcoded white. */
const Dot = styled.button<{ $active: boolean }>`
  width: 8px;
  height: 8px;
  flex: none;
  padding: 0;
  border: none;
  border-radius: 100px;
  cursor: pointer;
  background-color: ${({ $active }) =>
    $active ? "var(--comp-heading)" : "color-mix(in srgb, var(--comp-text) 30%, transparent)"};

  &:focus-visible {
    outline: 2px dashed var(--comp-heading);
    outline-offset: 2px;
  }
`

/* A video sitting in a mixed gallery. The single-media branch owns the only
   play/pause control, so a video shown alongside images had none at all
   (ASH-2124). Each video slide now carries its own, and plays only while its
   slide is active rather than every slide autoplaying at once underneath. */
function GalleryVideoSlide({
  url,
  isActive,
  isRtl,
}: {
  url: string
  isActive: boolean
  isRtl: boolean
}) {
  const analytics = useLandingAnalytics()
  const ref = useRef<HTMLVideoElement>(null)
  const [paused, setPaused] = useState(true)

  useEffect(() => {
    if (!ref.current) return
    if (isActive) {
      ref.current.currentTime = 0
      ref.current.play()
      setPaused(false)
    } else {
      ref.current.pause()
      setPaused(true)
    }
  }, [isActive])

  return (
    <>
      <MediaVideo
        ref={ref}
        src={getUmbracoMediaUrl(url)}
        muted
        loop
        playsInline
        onPlay={() => setPaused(false)}
        onPause={() => setPaused(true)}
      />
      <GalleryPlayPauseBtn
        type="button"
        onClick={() => {
          if (!ref.current) return
          analytics.mediaClick(paused ? "play" : "pause", LANDING_ELEMENTS.aboutOpening)
          if (paused) ref.current.play()
          else ref.current.pause()
        }}
        aria-label={
          paused ? (isRtl ? "הפעל וידאו" : "Play video") : isRtl ? "עצור וידאו" : "Pause video"
        }
      >
        {paused ? <PlayIcon /> : <PauseIcon />}
      </GalleryPlayPauseBtn>
    </>
  )
}

// --- Types & parsing ---

type MediaItem = { url: string; alt: string }
type LogoItem = { url: string; alt: string }
type LinkItem = { name: string; url: string; target?: string } | null

function isVideoUrl(url: string): boolean {
  // tolerate query strings / fragments (e.g. foo.mp4?width=680)
  return /\.(mp4|webm)(\?|#|$)/i.test(url)
}

function parseLink(raw: any): LinkItem {
  const link = Array.isArray(raw) ? raw[0] : undefined
  if (!link?.name) return null
  return {
    name: link.name,
    url: getLinkHrefFromMultiUrlPickerItem(link) || "#",
    target: link.target,
  }
}

// --- Main Component ---

const MAX_LOGOS = 3
const GALLERY_AUTOPLAY_MS = 5000

function AboutOpening6({ data }: Props) {
  const locale = useLocale()
  const isRtl = locale === "he" || !locale
  const analytics = useLandingAnalytics()
  const { ref, inView } = useInView(useInViewConfig)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(true)
  const [slide, setSlide] = useState(0)
  const [autoplay, setAutoplay] = useState(true)

  const projectName = (extractProperty(data, "projectName") as string) || ""
  const marketingHeadline = (extractProperty(data, "marketingHeadline") as string) || ""
  const shortDescription = (extractProperty(data, "shortDescription") as string) || ""
  const rawLogos = (extractProperty(data, "logos", true) as any[] | undefined) || []
  const logos: LogoItem[] = rawLogos
    .filter((m) => m?.url)
    .map((m) => ({ url: m.url as string, alt: (m.altText as string) || "" }))
    .slice(0, MAX_LOGOS)
  const rawMedia = (extractProperty(data, "media", true) as any[] | undefined) || []
  const mediaItems: MediaItem[] = rawMedia
    .filter((m) => m?.url)
    .map((m) => ({ url: m.url as string, alt: (m.altText as string) || "" }))
  const button1 = parseLink(extractProperty(data, "firstButton"))
  const button2 = parseLink(extractProperty(data, "secondButton"))

  const isVideo = mediaItems.length === 1 && isVideoUrl(mediaItems[0].url)
  const isGallery = mediaItems.length > 1

  // A gallery advances on its own; the play/pause between the arrows stops it
  // (WCAG 2.2.2). Reduced-motion users start paused rather than mid-slideshow.
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) setAutoplay(false)
  }, [])

  useEffect(() => {
    if (!isGallery || !autoplay) return
    const id = setInterval(() => setSlide((s) => (s + 1) % mediaItems.length), GALLERY_AUTOPLAY_MS)
    return () => clearInterval(id)
  }, [isGallery, autoplay, mediaItems.length])

  const togglePlay = () => {
    const v = videoRef.current
    if (!v) return
    analytics.mediaClick(isPlaying ? "pause" : "play", LANDING_ELEMENTS.aboutOpening)
    if (isPlaying) v.pause()
    else v.play()
    setIsPlaying(!isPlaying)
  }

  const go = (delta: number) => setSlide((s) => (s + delta + mediaItems.length) % mediaItems.length)

  if (!projectName && !marketingHeadline && !shortDescription && mediaItems.length === 0)
    return null

  const linkProps = (l: NonNullable<LinkItem>) =>
    l.target === "_blank" ? { target: "_blank", rel: "noopener noreferrer" } : {}

  const media = mediaItems.length > 0 && (
    <MediaSlot $in={inView} $delay={0}>
      <MediaFrame>
        <MediaColumn
          {...(isGallery
            ? {
                role: "group",
                "aria-roledescription": isRtl ? "גלריה" : "carousel",
                tabIndex: 0,
                onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
                  // ArrowLeft advances, ArrowRight goes back (mirror in LTR)
                  if (e.key === "ArrowLeft") go(isRtl ? 1 : -1)
                  else if (e.key === "ArrowRight") go(isRtl ? -1 : 1)
                },
              }
            : {})}
        >
          {isVideo ? (
            <>
              <MediaVideo
                ref={videoRef}
                src={getUmbracoMediaUrl(mediaItems[0].url)}
                autoPlay
                muted
                loop
                playsInline
              />
              <VideoControls>
                <PlayPauseBtn
                  type="button"
                  onClick={togglePlay}
                  aria-label={
                    isPlaying
                      ? isRtl
                        ? "עצור תנועת הרכיב"
                        : "Stop animation"
                      : isRtl
                      ? "הפעל תנועה רכיב"
                      : "Play animation"
                  }
                >
                  {isPlaying ? <PauseIcon /> : <PlayIcon />}
                </PlayPauseBtn>
              </VideoControls>
            </>
          ) : (
            <>
              {mediaItems.map((item, i) => (
                <Slide
                  key={i}
                  $active={i === slide}
                  aria-hidden={i !== slide || undefined}
                  {...(i !== slide ? { inert: "" } : {})}
                >
                  {isVideoUrl(item.url) ? (
                    <GalleryVideoSlide url={item.url} isActive={i === slide} isRtl={isRtl} />
                  ) : (
                    <MediaImage
                      src={getUmbracoMediaUrl(item.url)}
                      fill
                      alt={item.alt}
                      sizes="(max-width: 768px) 100vw, 680px"
                    />
                  )}
                </Slide>
              ))}
            </>
          )}
        </MediaColumn>
        {isGallery && (
          <>
            <GalleryControls>
              <ArrowButton
                type="button"
                onClick={() => go(-1)}
                aria-label={isRtl ? "הקודם" : "Previous"}
              >
                <ArrowIcon dir={isRtl ? "right" : "left"} />
              </ArrowButton>
              <PlayPauseBtn
                type="button"
                onClick={() => setAutoplay((a) => !a)}
                aria-label={
                  autoplay
                    ? isRtl
                      ? "עצור מצגת"
                      : "Pause slideshow"
                    : isRtl
                    ? "הפעל מצגת"
                    : "Play slideshow"
                }
              >
                {autoplay ? <PauseIcon /> : <PlayIcon />}
              </PlayPauseBtn>
              <ArrowButton type="button" onClick={() => go(1)} aria-label={isRtl ? "הבא" : "Next"}>
                <ArrowIcon dir={isRtl ? "left" : "right"} />
              </ArrowButton>
            </GalleryControls>
            <Dots>
              {mediaItems.map((_, i) => (
                <Dot
                  key={i}
                  type="button"
                  $active={i === slide}
                  onClick={() => setSlide(i)}
                  aria-current={i === slide}
                  aria-label={isRtl ? `תמונה ${i + 1}` : `Image ${i + 1}`}
                />
              ))}
            </Dots>
          </>
        )}
      </MediaFrame>
    </MediaSlot>
  )

  const content = (
    <TextColumn>
      {logos.length > 0 && (
        <Reveal $in={inView} $delay={0}>
          <Logos>
            {logos.map((logo, i) => (
              <Logo key={i} src={getUmbracoMediaUrl(logo.url)} alt={logo.alt} />
            ))}
          </Logos>
        </Reveal>
      )}
      {(projectName || marketingHeadline) && (
        <TitleGroup>
          {projectName && (
            <Reveal $in={inView} $delay={140}>
              <Title dangerouslySetInnerHTML={{ __html: projectName }} />
            </Reveal>
          )}
          {marketingHeadline && (
            <Reveal $in={inView} $delay={280}>
              <MarketingHeadline>{marketingHeadline}</MarketingHeadline>
            </Reveal>
          )}
        </TitleGroup>
      )}
      {shortDescription && (
        <Reveal $in={inView} $delay={420}>
          <Description>{shortDescription}</Description>
        </Reveal>
      )}
      {(button1 || button2) && (
        <Reveal $in={inView} $delay={560}>
          <Actions>
            {button1 && (
              <CtaButton
                href={button1.url}
                onClick={(e) => {
                  analytics.ctaClick(button1.name, LANDING_ELEMENTS.aboutOpening)
                  handleAnchorScroll(e, button1.url)
                }}
                {...linkProps(button1)}
              >
                {button1.name}
              </CtaButton>
            )}
            {button2 && (
              <CtaButton
                $filled
                href={button2.url}
                onClick={(e) => {
                  analytics.ctaClick(button2.name, LANDING_ELEMENTS.aboutOpening)
                  handleAnchorScroll(e, button2.url)
                }}
                {...linkProps(button2)}
              >
                {button2.name}
              </CtaButton>
            )}
          </Actions>
        </Reveal>
      )}
    </TextColumn>
  )

  return (
    <Section ref={ref}>
      <Inner>
        {/* content first in JSX = reading-start (right) on desktop; media to the
            left. On mobile the column stacks content then media. */}
        {content}
        {media}
      </Inner>
    </Section>
  )
}

export default AboutOpening6
